import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { useMemo, forwardRef } from "react";
import { DoneIcon, ArrowRightSmallIcon, ArrowDownSmallIcon } from "@blocksuite/icons";
import { a as assignInlineVars } from "./vanilla-extract-dynamic.esm-b0bca947.js";
const styles_css_ts_vanilla = "";
var menuContent$1 = "_1cquml1";
var menuSeparator = "_1cquml5";
const Menu = ({
  children,
  items,
  portalOptions,
  rootOptions,
  contentOptions: {
    className = "",
    style: contentStyle = {},
    ...otherContentOptions
  } = {}
}) => {
  return /* @__PURE__ */ jsxs(DropdownMenu.Root, { ...rootOptions, children: [
    /* @__PURE__ */ jsx(DropdownMenu.Trigger, { asChild: true, children }),
    /* @__PURE__ */ jsx(DropdownMenu.Portal, { ...portalOptions, children: /* @__PURE__ */ jsx(
      DropdownMenu.Content,
      {
        className: useMemo(
          () => clsx(menuContent$1, className),
          [className]
        ),
        sideOffset: 5,
        align: "start",
        style: { zIndex: "var(--affine-z-index-popover)", ...contentStyle },
        ...otherContentOptions,
        children: items
      }
    ) })
  ] });
};
var menuContent = "_1cquml1";
var menuItem = "_1cquml2";
var menuItemIcon = "_1cquml4";
var menuSpan = "_1cquml3";
var menuTrigger = "_1cquml6";
var triggerWidthVar = "var(--_1cquml0)";
const MenuIcon = forwardRef(
  ({ children, icon, position = "start", className, ...otherProps }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: useMemo(
          () => clsx(
            menuItemIcon,
            {
              end: position === "end",
              start: position === "start"
            },
            className
          ),
          [className, position]
        ),
        ...otherProps,
        children: icon || children
      }
    );
  }
);
MenuIcon.displayName = "MenuIcon";
const useMenuItem = ({
  children: propsChildren,
  type = "default",
  className: propsClassName,
  preFix,
  endFix,
  checked,
  selected,
  block
}) => {
  const className = useMemo(
    () => clsx(
      menuItem,
      {
        danger: type === "danger",
        warning: type === "warning",
        checked,
        selected,
        block
      },
      propsClassName
    ),
    [block, checked, propsClassName, selected, type]
  );
  const children = useMemo(
    () => /* @__PURE__ */ jsxs(Fragment, { children: [
      preFix,
      /* @__PURE__ */ jsx("span", { className: menuSpan, children: propsChildren }),
      endFix,
      checked || selected ? /* @__PURE__ */ jsx(
        MenuIcon,
        {
          position: "end",
          className: clsx({
            selected,
            checked
          }),
          children: /* @__PURE__ */ jsx(DoneIcon, {})
        }
      ) : null
    ] }),
    [checked, endFix, preFix, propsChildren, selected]
  );
  return {
    children,
    className
  };
};
const MenuItem = ({
  children: propsChildren,
  type = "default",
  className: propsClassName,
  preFix,
  endFix,
  checked,
  selected,
  block,
  ...otherProps
}) => {
  const { className, children } = useMenuItem({
    children: propsChildren,
    className: propsClassName,
    type,
    preFix,
    endFix,
    checked,
    selected,
    block
  });
  return /* @__PURE__ */ jsx(DropdownMenu.Item, { className, ...otherProps, children });
};
const MenuSeparator = ({
  className,
  ...otherProps
}) => {
  return /* @__PURE__ */ jsx(
    DropdownMenu.Separator,
    {
      className: useMemo(() => clsx(menuSeparator, className), [className]),
      ...otherProps
    }
  );
};
const MenuSub = ({
  children: propsChildren,
  items,
  portalOptions,
  subOptions,
  triggerOptions: {
    className: propsClassName,
    preFix,
    endFix,
    type,
    ...otherTriggerOptions
  } = {},
  subContentOptions: {
    className: subContentClassName = "",
    ...otherSubContentOptions
  } = {}
}) => {
  const { className, children } = useMenuItem({
    children: propsChildren,
    className: propsClassName,
    type,
    preFix,
    endFix
  });
  return /* @__PURE__ */ jsxs(DropdownMenu.Sub, { ...subOptions, children: [
    /* @__PURE__ */ jsxs(DropdownMenu.SubTrigger, { className, ...otherTriggerOptions, children: [
      children,
      /* @__PURE__ */ jsx(MenuIcon, { position: "end", children: /* @__PURE__ */ jsx(ArrowRightSmallIcon, {}) })
    ] }),
    /* @__PURE__ */ jsx(DropdownMenu.Portal, { ...portalOptions, children: /* @__PURE__ */ jsx(
      DropdownMenu.SubContent,
      {
        sideOffset: 10,
        className: useMemo(
          () => clsx(menuContent, subContentClassName),
          [subContentClassName]
        ),
        ...otherSubContentOptions,
        children: items
      }
    ) })
  ] });
};
const MenuTrigger = forwardRef(
  ({
    disabled,
    noBorder = false,
    className,
    status = "default",
    size = "default",
    preFix,
    endFix,
    block = false,
    children,
    width,
    style = {},
    ...otherProps
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        style: {
          ...assignInlineVars({
            [triggerWidthVar]: width ? typeof width === "number" ? `${width}px` : width : "auto"
          }),
          ...style
        },
        className: clsx(menuTrigger, className, {
          // status
          block,
          disabled,
          "no-border": noBorder,
          // color
          error: status === "error",
          success: status === "success",
          warning: status === "warning",
          default: status === "default",
          // size
          large: size === "large",
          "extra-large": size === "extraLarge"
        }),
        ...otherProps,
        children: [
          preFix,
          children,
          endFix,
          /* @__PURE__ */ jsx(MenuIcon, { position: "end", children: /* @__PURE__ */ jsx(ArrowDownSmallIcon, {}) })
        ]
      }
    );
  }
);
MenuTrigger.displayName = "MenuTrigger";
export {
  Menu,
  MenuIcon,
  MenuItem,
  MenuSeparator,
  MenuSub,
  MenuTrigger
};
